import { judge } from "@volca/ui/trades";
import { reportNumber } from "@/profitability/report-number";
import {
  COLUMN_ROLE,
  type ReportColumn,
  type ReportRow,
  type ReportValue,
} from "@/profitability/types";

/**
 * Which way is good news for a column: what its script said, else down for a
 * cost and up for everything else.
 */
export const betterOf = (
  column: Pick<ReportColumn, "better" | "role">,
): "higher" | "lower" =>
  column.better ?? (column.role === COLUMN_ROLE.cost ? "lower" : "higher");

export const estimateFor = (
  columns: ReportColumn[],
  actual: Pick<ReportColumn, "key">,
) => columns.find((column) => column.estimate_of === actual.key);

export interface Variance {
  actual: number | null;
  estimate: number | null;
  /** Actual less estimate; null when either is missing. */
  change: number | null;
  /** The change over the estimate's size; null when it cannot be said. */
  ratio: number | null;
  tone: "good" | "bad" | "neutral";
}

/**
 * How an actual figure came out against its estimate. A cost over its
 * estimate is bad news, revenue over its estimate good, as the actual
 * column's `better` says.
 */
export const variance = (
  actualValue: ReportValue | undefined,
  estimateValue: ReportValue | undefined,
  actual: Pick<ReportColumn, "better" | "role">,
): Variance => {
  const real = reportNumber(actualValue);
  const estimate = reportNumber(estimateValue);
  const change = real !== null && estimate !== null ? real - estimate : null;
  const ratio =
    change !== null && estimate !== null && estimate !== 0
      ? change / Math.abs(estimate)
      : null;
  return {
    actual: real,
    estimate,
    change,
    ratio,
    tone: judge(change, betterOf(actual)),
  };
};

export const rowVariance = (
  columns: ReportColumn[],
  actual: ReportColumn,
  row: ReportRow,
) => {
  const estimate = estimateFor(columns, actual);
  return estimate
    ? {
        estimate,
        variance: variance(row[actual.key], row[estimate.key], actual),
      }
    : null;
};

/**
 * The columns a table shows once each estimate rides in its actual's cell:
 * an estimate whose actual is shown goes, unless it was asked for by name
 * (`explicit`, the keys a script listed in the block itself).
 */
export const withoutShownEstimates = <T extends Pick<ReportColumn, "key">>(
  columns: (T & Pick<ReportColumn, "estimate_of">)[],
  explicit: string[] = [],
) => {
  const shown = new Set(columns.map((column) => column.key));
  return columns.filter(
    (column) =>
      !column.estimate_of ||
      !shown.has(column.estimate_of) ||
      explicit.includes(column.key),
  );
};

/** "+12.3%", "−4.0%": a variance as a signed share of the estimate. */
export const formatRatio = (ratio: number) =>
  `${ratio < 0 ? "−" : "+"}${Math.abs(ratio * 100).toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
